/**
 * P41 · the staff's FULL panel — grant/revoke WITH STATE.
 *
 * Until today, `GuardianLinkDTO` (`GET /api/org/listGuardians`) carried nothing
 * about consent: the "Guardians" tab of the expediente could only request
 * authorization by email (`request-consent-panel.tsx`) and warn about a
 * missing date of birth. A "Revoke" button unable to say whether anything had
 * been granted is exactly the class of interface that looks like it's watching
 * and isn't — that's what the interface acta of 2026-08-15, §4 wrote down. With
 * the DTO's five new columns (`consent_status` plus its four companions, see
 * `models/org.rs::GuardianLinkView`), this component closes the two actions
 * that were missing: **recording** a consent gathered in person or on paper,
 * and **revoking** it.
 *
 * ── The revoke button ONLY exists when the state that arrived is `granted` ──
 *
 * There isn't even an `if` that re-derives "is there something to withdraw?"
 * from dates or scopes: it literally checks `consentStatus === "granted"`, the
 * FACT the server already resolved. Offering "Revoke" over a `pending` link
 * would be offering an action with no object — it's one of the layers this
 * front's anti-regression proof requires catching.
 *
 * ── Why only `in_person` and `paper` ─────────────────────────────────────────
 *
 * Staff are FORBIDDEN from declaring the `email` method: that value is written
 * by `use_cases::consent::confirm` when the guardian themself confirms through
 * double opt-in, and the server rejects it if it arrives from this endpoint
 * (there's a test: `staff_cannot_sign_on_the_guardians_behalf`). That's why this
 * form doesn't even offer the option: requesting it by email already has its
 * own door (`request-consent-panel.tsx`).
 *
 * ── What it does NOT do ───────────────────────────────────────────────────────
 *
 * It doesn't decide whether the link SHOULD be authorized nor why a specific
 * use is blocked: that's `ConsentDenial`'s six reasons, which by design never
 * reach the client (see `my-guardianship-view.tsx`'s header). Here only facts
 * get rendered and written.
 */
import { msg } from "@lingui/core/macro";
import { type Component, createSignal, For, Show } from "solid-js";
import { handleApiResponse } from "@space/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Badge } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { showToast } from "@/components/ui/toast";
import {
  CONSENT_SCOPE_LABELS,
  CONSENT_STATUS_LABELS,
  DEFAULT_CONSENT_SCOPES,
  labelFor,
  OFFERED_CONSENT_SCOPES,
} from "./labels";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

/** The facts about a `guardian_link` this component needs to render. */
export interface GuardianConsentLinkFacts {
  id: string;
  consentStatus: string;
  consentScopes: readonly string[];
  consentGrantedAt?: string | null;
  consentRevokedAt?: string | null;
}

export const GuardianConsentActions: Component<{
  link: GuardianConsentLinkFacts;
  canManage: boolean;
  onChanged: () => void;
}> = (props) => {
  const { t, locale } = useI18n();
  const [busy, setBusy] = createSignal(false);
  const [formOpen, setFormOpen] = createSignal(false);
  const [confirmRevoke, setConfirmRevoke] = createSignal(false);
  const [method, setMethod] = createSignal<"in_person" | "paper">("in_person");
  const [scopes, setScopes] = createSignal<string[]>([...DEFAULT_CONSENT_SCOPES]);
  const [evidenceRef, setEvidenceRef] = createSignal("");

  // FACT, not a re-derived decision: the server already resolved `consentStatus`.
  const isGranted = () => props.link.consentStatus === "granted";

  const toggleScope = (scope: string) => {
    setScopes((current) =>
      current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope],
    );
  };

  const formatDate = (iso: string | null | undefined) =>
    iso ? new Intl.DateTimeFormat(locale()).format(new Date(iso)) : null;

  const grant = async () => {
    if (busy() || scopes().length === 0) {
      return;
    }
    setBusy(true);
    try {
      await handleApiResponse(
        api.POST("/api/org/guardianConsent/grant", {
          body: {
            linkId: props.link.id,
            scopes: scopes(),
            method: method(),
            evidenceRef: evidenceRef().trim() || null,
          },
        }),
      );
      showToast(t(msg`Consent recorded`), "success");
      setFormOpen(false);
      setEvidenceRef("");
      props.onChanged();
    } catch {
      showToast(t(msg`Could not record the consent`), "error");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await handleApiResponse(
        api.POST("/api/org/guardianConsent/revoke", { body: { linkId: props.link.id } }),
      );
      showToast(t(msg`Consent withdrawn`), "success");
      props.onChanged();
    } catch {
      showToast(t(msg`Could not withdraw the consent`), "error");
    } finally {
      setBusy(false);
      setConfirmRevoke(false);
    }
  };

  return (
    <div
      class="space-y-2 rounded-xl border border-border/60 p-3"
      data-testid="guardian-consent-status"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <Show when={labelFor(CONSENT_STATUS_LABELS, props.link.consentStatus)}>
          {(label) => (
            <Badge
              variant={isGranted() ? "outline" : "secondary"}
              data-testid="guardian-consent-badge"
            >
              {t(label())}
            </Badge>
          )}
        </Show>
        {/*
          The button ONLY appears when the literal state is `granted`. A
          `pending` link has nothing to withdraw, and neither does a `revoked`
          one — even though it keeps its old scopes for the record
          (db::consent::revoke) — so showing "Revoke" there would be offering
          an action with no object.
        */}
        <Show when={props.canManage && isGranted()}>
          <Button
            variant="ghost"
            size="sm"
            data-testid="guardian-consent-revoke"
            disabled={busy()}
            onClick={() => {
              setConfirmRevoke(true);
            }}
          >
            {t(msg`Revoke`)}
          </Button>
        </Show>
      </div>

      <Show when={props.link.consentScopes.length > 0}>
        <p class="text-xs text-muted-foreground" data-testid="guardian-consent-record-scopes">
          {t(msg`Authorized for:`)}{" "}
          <For each={props.link.consentScopes}>
            {(scope, i) => (
              <Show when={labelFor(CONSENT_SCOPE_LABELS, scope)}>
                {(label) => (
                  <span>
                    <Show when={i() > 0}>{" · "}</Show>
                    {t(label())}
                  </span>
                )}
              </Show>
            )}
          </For>
        </p>
      </Show>

      <Show when={isGranted() && formatDate(props.link.consentGrantedAt)}>
        {(date) => {
          const value = date();
          return <p class="text-xs text-muted-foreground">{t(msg`Authorized on ${value}`)}</p>;
        }}
      </Show>
      <Show when={!isGranted() && formatDate(props.link.consentRevokedAt)}>
        {(date) => {
          const value = date();
          return <p class="text-xs text-muted-foreground">{t(msg`Withdrawn on ${value}`)}</p>;
        }}
      </Show>

      <Show when={props.canManage && !isGranted()}>
        <Show
          when={formOpen()}
          fallback={
            <Button
              variant="outline"
              size="sm"
              data-testid="guardian-consent-open-grant"
              onClick={() => {
                setFormOpen(true);
              }}
            >
              {t(msg`Record consent obtained in person or on paper`)}
            </Button>
          }
        >
          <div class="space-y-2 rounded-lg border border-dashed border-border p-3">
            <fieldset class="flex flex-wrap gap-3 text-xs">
              <legend class="mb-1 w-full text-xs text-muted-foreground">
                {t(msg`How was it obtained`)}
              </legend>
              <label class="flex items-center gap-1">
                <input
                  type="radio"
                  name={`guardian-consent-method-${props.link.id}`}
                  data-testid="guardian-consent-method-in-person"
                  checked={method() === "in_person"}
                  onChange={() => {
                    setMethod("in_person");
                  }}
                />
                {t(msg`In person, with an ID shown`)}
              </label>
              <label class="flex items-center gap-1">
                <input
                  type="radio"
                  name={`guardian-consent-method-${props.link.id}`}
                  data-testid="guardian-consent-method-paper"
                  checked={method() === "paper"}
                  onChange={() => {
                    setMethod("paper");
                  }}
                />
                {t(msg`Signed paper form on file`)}
              </label>
            </fieldset>

            <fieldset class="space-y-1">
              <legend class="text-xs text-muted-foreground">
                {t(msg`What is being authorized`)}
              </legend>
              <div class="flex flex-wrap gap-3">
                <For each={OFFERED_CONSENT_SCOPES}>
                  {(scope) => (
                    <Show when={labelFor(CONSENT_SCOPE_LABELS, scope)}>
                      {(label) => (
                        <label class="flex items-center gap-1 text-xs">
                          <input
                            type="checkbox"
                            data-testid={`guardian-consent-grant-scope-${scope}`}
                            checked={scopes().includes(scope)}
                            onChange={() => {
                              toggleScope(scope);
                            }}
                          />
                          {t(label())}
                        </label>
                      )}
                    </Show>
                  )}
                </For>
              </div>
            </fieldset>

            <label class="block space-y-1 text-xs text-muted-foreground">
              {t(msg`Proof reference (optional)`)}
              <input
                class={field}
                data-testid="guardian-consent-evidence"
                value={evidenceRef()}
                onInput={(e) => {
                  setEvidenceRef(e.currentTarget.value);
                }}
                placeholder={t(msg`e.g. ID shown, file number`)}
              />
            </label>

            <div class="flex gap-2">
              <Button
                size="sm"
                data-testid="guardian-consent-grant"
                disabled={busy() || scopes().length === 0}
                onClick={() => void grant()}
              >
                {t(msg`Record consent`)}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFormOpen(false);
                }}
              >
                {t(msg`Cancel`)}
              </Button>
            </div>
          </div>
        </Show>
      </Show>

      <ConfirmDialog
        open={confirmRevoke()}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmRevoke(false);
          }
        }}
        title={t(msg`Withdraw this consent?`)}
        description={t(
          msg`The guardian's authorization stops applying from the student's next action. This does not erase the record that it once existed.`,
        )}
        confirmLabel={t(msg`Revoke`)}
        variant="destructive"
        isLoading={busy()}
        onConfirm={() => void revoke()}
      />
    </div>
  );
};
